import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const superAdminApi = createApi({
  reducerPath: 'superAdminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_URL}/super-admin`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Dashboard', 'Company', 'Plan', 'Subscription', 'Payment', 'Inquiry'],
  endpoints: (builder) => ({
    // Dashboard
    getDashboardStats: builder.query({
      query: () => '/dashboard',
      providesTags: ['Dashboard'],
    }),

    // Companies
    getAllCompanies: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          if (params[key]) searchParams.append(key, params[key]);
        });
        return `/companies?${searchParams.toString()}`;
      },
      providesTags: ['Company'],
    }),

    getCompanyDetails: builder.query({
      query: (id) => `/companies/${id}`,
      providesTags: (result, error, id) => [{ type: 'Company', id }],
    }),

    updateCompany: builder.mutation({
      query: ({ id, ...company }) => ({
        url: `/companies/${id}`,
        method: 'PUT',
        body: company,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Company', id },
        'Company',
        'Dashboard',
      ],
    }),

    deleteCompany: builder.mutation({
      query: (id) => ({
        url: `/companies/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Company', 'Dashboard'],
    }),

    hardDeleteCompany: builder.mutation({
      query: ({ id, password }) => ({
        url: `/companies/${id}/hard-delete`,
        method: 'DELETE',
        body: { password },
      }),
      invalidatesTags: ['Company', 'Dashboard'],
    }),

    toggleSubscription: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/companies/${id}/subscription/toggle`,
        method: 'PUT',
        body: { reason },
      }),
      invalidatesTags: ['Company', 'Dashboard'],
    }),

    // Plans
    getAllPlans: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          if (params[key]) searchParams.append(key, params[key]);
        });
        return `/plans?${searchParams.toString()}`;
      },
      providesTags: ['Plan'],
    }),

    getPlanDetails: builder.query({
      query: (id) => `/plans/${id}`,
      providesTags: (result, error, id) => [{ type: 'Plan', id }],
    }),

    createPlan: builder.mutation({
      query: (plan) => ({
        url: '/plans',
        method: 'POST',
        body: plan,
      }),
      invalidatesTags: ['Plan', 'Dashboard'],
    }),

    updatePlan: builder.mutation({
      query: ({ id, ...plan }) => ({
        url: `/plans/${id}`,
        method: 'PUT',
        body: plan,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Plan', id },
        'Plan',
        'Dashboard',
      ],
    }),

    deletePlan: builder.mutation({
      query: (id) => ({
        url: `/plans/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Plan', 'Dashboard'],
    }),

    togglePlanStatus: builder.mutation({
      query: (id) => ({
        url: `/plans/${id}/toggle-status`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Plan', id },
        'Plan',
        'Dashboard',
      ],
    }),

    duplicatePlan: builder.mutation({
      query: (id) => ({
        url: `/plans/${id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: ['Plan', 'Dashboard'],
    }),

    // Subscriptions
    updateSubscription: builder.mutation({
      query: ({ id, ...subscription }) => ({
        url: `/subscriptions/${id}`,
        method: 'PUT',
        body: subscription,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Subscription', id },
        'Company',
        'Dashboard',
      ],
    }),

    getUpcomingExpirations: builder.query({
      query: () => '/subscriptions/upcoming-expirations',
      providesTags: ['Subscription'],
    }),

    updateSubscriptionDates: builder.mutation({
      query: ({ companyId, subscriptionData }) => ({
        url: `/companies/${companyId}/subscription-dates`,
        method: 'PUT',
        body: subscriptionData,
      }),
      invalidatesTags: ['Company', 'Subscription', 'Dashboard'],
    }),

    // Payment System
    getBankDetails: builder.query({
      query: () => '/payment/bank-details',
      providesTags: ['Payment'],
    }),

    updateBankDetails: builder.mutation({
      query: (bankDetails) => ({
        url: '/payment/bank-details',
        method: 'PUT',
        body: bankDetails,
      }),
      invalidatesTags: ['Payment'],
    }),

    generatePaymentQR: builder.mutation({
      query: (paymentData) => ({
        url: '/payment/generate-qr',
        method: 'POST',
        body: paymentData,
      }),
    }),

    generateSubscriptionQR: builder.mutation({
      query: (subscriptionId) => ({
        url: `/payment/subscription-qr/${subscriptionId}`,
        method: 'POST',
      }),
    }),

    validatePayment: builder.mutation({
      query: (paymentData) => ({
        url: '/payment/validate',
        method: 'POST',
        body: paymentData,
      }),
      invalidatesTags: ['Payment', 'Company', 'Dashboard'],
    }),

    getPaymentHistory: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          if (params[key]) searchParams.append(key, params[key]);
        });
        return `/payment/history?${searchParams.toString()}`;
      },
      providesTags: ['Payment'],
    }),

    // New payment verification endpoints
    getPendingPayments: builder.query({
      query: () => '/payment/pending',
      providesTags: ['Payment'],
    }),

    verifyPayment: builder.mutation({
      query: ({ paymentId, notes }) => ({
        url: `/payment/${paymentId}/verify`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: ['Payment', 'Company', 'Dashboard'],
    }),

    rejectPayment: builder.mutation({
      query: ({ paymentId, reason }) => ({
        url: `/payment/${paymentId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Payment', 'Company', 'Dashboard', 'Subscription'],
    }),

    // Inquiries
    getAllInquiries: builder.query({
      query: () => '/inquiries',
      providesTags: ['Inquiry'],
    }),

    updateInquiry: builder.mutation({
      query: ({ id, ...inquiry }) => ({
        url: `/inquiries/${id}`,
        method: 'PUT',
        body: inquiry,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Inquiry', id },
        'Inquiry',
      ],
    }),

    deleteInquiry: builder.mutation({
      query: (id) => ({
        url: `/inquiries/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Inquiry'],
    }),

    // Data Cleanup
    cleanupOrphanedData: builder.mutation({
      query: (cleanupData) => ({
        url: '/cleanup-orphaned-data',
        method: 'POST',
        body: cleanupData,
      }),
      invalidatesTags: ['Dashboard'],
    }),
  }),
});

export const {
  // Dashboard
  useGetDashboardStatsQuery,
  
  // Companies
  useGetAllCompaniesQuery,
  useGetCompanyDetailsQuery,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useHardDeleteCompanyMutation,
  useToggleSubscriptionMutation,
  
  // Plans
  useGetAllPlansQuery,
  useGetPlanDetailsQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
  useTogglePlanStatusMutation,
  useDuplicatePlanMutation,
  
  // Subscriptions
  useUpdateSubscriptionMutation,
  useGetUpcomingExpirationsQuery,
  useUpdateSubscriptionDatesMutation,
  
  // Payment System
  useGetBankDetailsQuery,
  useUpdateBankDetailsMutation,
  useGeneratePaymentQRMutation,
  useGenerateSubscriptionQRMutation,
  useValidatePaymentMutation,
  useGetPaymentHistoryQuery,
  useGetPendingPaymentsQuery,
  useVerifyPaymentMutation,
  useRejectPaymentMutation,

  // Inquiries
  useGetAllInquiriesQuery,
  useUpdateInquiryMutation,
  useDeleteInquiryMutation,

  // Data Cleanup
  useCleanupOrphanedDataMutation,
} = superAdminApi;